import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sparkles, X, Loader2, RefreshCw, AlertTriangle,
  TrendingUp, TrendingDown, ShieldCheck, ListChecks,
  Briefcase, FlaskConical, Users,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ScoringRubricModal from './ScoringRubricModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────
// ExecutiveSummaryModal (Feb 2026)
// Replaces the old DeepDiveModal placeholder that fired from the
// Verdict cell click in the Cohort Scoring Matrix. Surfaces the
// synthesised board-memo brief produced by services/viatris_exec_summary.py
// — triangulating scientific assessment + committee consensus +
// AI concerns + ForesightRx Opportunity into ONE board-grade narrative.
//
// Opens with the committee 360° already cached server-side; renders
// a degraded skeleton + Retry CTA if Claude was down at the time of
// the first request.
// ─────────────────────────────────────────────────────────────────────

const VERDICT_BADGE = {
  'In-License': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Reassess':   'bg-amber-100 text-amber-900 border-amber-300',
  'Decline':    'bg-rose-100 text-rose-900 border-rose-300',
};

export default function ExecutiveSummaryModal({ token, asset, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [rubricOpen, setRubricOpen] = useState(false);

  const load = React.useCallback(async (force = false) => {
    if (force) setRegenerating(true);
    else setLoading(true);
    setError(null);
    try {
      const url = force
        ? `${API}/client/viatris/${token}/assets/${asset.ea_id}/executive-summary/regenerate`
        : `${API}/client/viatris/${token}/assets/${asset.ea_id}/executive-summary`;
      const r = force
        ? await axios.post(url)
        : await axios.get(url);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load Executive Summary');
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, [token, asset.ea_id]);

  useEffect(() => { load(false); }, [asset.ea_id, load]);

  const content = data?.content || {};
  const agg = data?.committee_aggregate || {};
  const verdict = data?.derived_verdict;
  const verdictCls = VERDICT_BADGE[verdict] || 'bg-slate-100 text-slate-700 border-slate-300';

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      {rubricOpen && <ScoringRubricModal onClose={() => setRubricOpen(false)} />}
      <div
        className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="executive-summary-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-br from-indigo-50/60 to-white">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-700 font-semibold inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Executive Summary · Board-Memo Brief
              </div>
              <h2 className="text-xl font-bold text-slate-800 mt-1 leading-tight truncate" title={asset.asset_name}>
                {asset.asset_name}
              </h2>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {asset.indication || '—'} · {asset.modality || '—'} · {asset.biotech_name || asset.biotech_company_name || '—'}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {verdict && (
                  <Badge className={`text-[11px] border font-semibold ${verdictCls}`} data-testid="exec-summary-verdict-badge">
                    {verdict}
                  </Badge>
                )}
                {/* Feb 2026 — Overall /50 badge retired from client-visible portal (FRx removed). */}
                {agg?.overall_mean != null && (
                  <span className="inline-flex items-baseline gap-0.5 text-xs px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800" title="Committee weighted average across the 9 scientific dimensions (mean over all scored votes)">
                    <Users className="w-3 h-3 mr-0.5" />
                    <span className="font-bold">{agg.overall_mean}</span>
                    <span className="text-[10px] opacity-70">/5 Committee</span>
                  </span>
                )}
                {agg?.dispersion_flag && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800"
                    title="At least one dimension has a vote spread of ≥ 2 — committee is split"
                  >
                    <AlertTriangle className="w-2.5 h-2.5" /> Split house
                  </span>
                )}
                {content?._degraded && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-700">
                    <AlertTriangle className="w-2.5 h-2.5" /> Synthesis incomplete — hit Regenerate
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => load(true)}
                disabled={loading || regenerating}
                className="text-xs"
                data-testid="exec-summary-regenerate"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Regenerating…' : 'Regenerate'}
              </Button>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 p-1"
                data-testid="exec-summary-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Math-anchor legend (Feb 2026) — mirrors the Cohort Scoring
            Matrix header + Deep Dive modal so verdict framing is
            consistent wherever the reviewer sees a tier. Slim variant
            (bordered strip) so it doesn't distract from the synthesis. */}
        <div className="px-6 py-2 border-b border-slate-100 bg-slate-50/60">
          <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
            <span className="text-slate-500 font-medium mr-1">Math anchors ·</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-800 font-medium">Strategic ≥ 4.5</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-teal-100 border border-teal-300 text-teal-800 font-medium">High 3.75–4.5</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 font-medium">Medium 3.0–3.75</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 border border-orange-300 text-orange-800 font-medium">Monitor 2.0–3.0</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-100 border border-rose-300 text-rose-800 font-medium">Low &lt; 2.0</span>
            <span className="text-slate-400">on Weighted Avg / 5</span>
          </div>
          <div className="text-[10px] text-slate-500 italic mt-0.5">
            Displayed verdict is an AI-holistic call. The math anchors above are the score baseline; the synthesis below explains any divergence.{' '}
            <button
              type="button"
              onClick={() => setRubricOpen(true)}
              className="not-italic font-semibold text-indigo-700 hover:text-indigo-900 underline decoration-dotted underline-offset-2 ml-0.5"
              data-testid="open-scoring-rubric-execsummary"
            >
              See scoring rubric →
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 text-sm">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
              <Loader2 className="w-7 h-7 animate-spin text-indigo-600" />
              <div className="text-xs">Synthesising 360° board-memo brief…</div>
            </div>
          ) : error ? (
            <div className="text-rose-700 text-sm border border-rose-200 bg-rose-50 rounded p-4" data-testid="exec-summary-error">
              <div className="font-semibold mb-1">Synthesis unavailable</div>
              <div>{error}</div>
            </div>
          ) : (
            <>
              {/* Headline + one-liner */}
              <section>
                <h3 className="text-base font-bold text-slate-800 leading-snug" data-testid="exec-summary-headline">
                  {content.headline}
                </h3>
                <p className="text-sm text-slate-600 mt-1.5 italic" data-testid="exec-summary-one-liner">
                  {content.one_line_summary}
                </p>
              </section>

              {/* Verdict rationale callout */}
              {content.verdict_rationale && (
                <section className="border-l-4 border-indigo-400 bg-indigo-50/40 rounded-r pl-4 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">Verdict Rationale</div>
                  <p className="text-sm text-slate-800 leading-relaxed" data-testid="exec-summary-verdict-rationale">
                    {content.verdict_rationale}
                  </p>
                </section>
              )}

              {/* Two-column case (scientific + commercial) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Section icon={FlaskConical} title="Scientific Case" testid="exec-summary-scientific-case">
                  {content.scientific_case}
                </Section>
                <Section icon={Briefcase} title="Commercial Case" testid="exec-summary-commercial-case">
                  {content.commercial_case}
                </Section>
              </div>

              {/* (Feb 2026) FRx Opportunity block hidden from
                  client-visible portal — internal-only. */}

              {/* Committee Signal */}
              {content.committee_signal && (
                <section data-testid="exec-summary-committee-signal">
                  <div className="text-xs uppercase tracking-wide text-slate-700 font-semibold mb-2 inline-flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-indigo-600" /> Committee Signal
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mb-3">
                    {content.committee_signal.consensus_observations}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <DimensionList
                      icon={TrendingUp}
                      tone="emerald"
                      title="Highest-scoring dimensions"
                      items={content.committee_signal.strongest_dimensions}
                      testid="exec-summary-strongest"
                    />
                    <DimensionList
                      icon={TrendingDown}
                      tone="rose"
                      title="Weakest-scoring dimensions"
                      items={content.committee_signal.weakest_dimensions}
                      testid="exec-summary-weakest"
                    />
                  </div>

                  {/* Per-dim score bars */}
                  {agg?.by_dim && (
                    <div className="mt-4 bg-slate-50 border border-slate-200 rounded p-3" data-testid="exec-summary-dim-bars">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
                        Per-dimension consensus (mean · range)
                      </div>
                      <div className="space-y-1.5">
                        {Object.entries(agg.by_dim).map(([key, d]) => (
                          <div key={key} className="flex items-center gap-2 text-xs">
                            <div className="w-44 text-slate-600 truncate" title={d.label}>{d.label}</div>
                            <div className="flex-1 bg-white border border-slate-200 rounded h-3 relative overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-indigo-400"
                                style={{ width: `${((d.mean || 0) / 5) * 100}%` }}
                              />
                            </div>
                            <div className="w-20 text-right font-mono text-slate-700">
                              {d.mean != null ? d.mean.toFixed(2) : '—'}
                              <span className="text-[10px] text-slate-400 ml-1">±{d.range != null ? d.range : '—'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Strengths / Risks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BulletBox tone="emerald" title="Key Strengths" items={content.key_strengths} testid="exec-summary-strengths" />
                <BulletBox tone="rose"    title="Key Risks"     items={content.key_risks}     testid="exec-summary-risks" />
              </div>

              {/* Reconciled AI Concerns */}
              {(content.reconciled_concerns || []).length > 0 && (
                <Section icon={AlertTriangle} title="Reconciled Concerns" testid="exec-summary-reconciled">
                  <ul className="list-disc pl-5 space-y-1.5">
                    {content.reconciled_concerns.map((c, i) => (
                      <li key={i} className="text-sm text-slate-700 leading-relaxed">{c}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Recommended Actions */}
              {(content.recommended_actions || []).length > 0 && (
                <section className="border border-indigo-200 bg-indigo-50/40 rounded p-4" data-testid="exec-summary-actions">
                  <div className="text-xs uppercase tracking-wide text-indigo-700 font-semibold mb-2 inline-flex items-center gap-1.5">
                    <ListChecks className="w-3.5 h-3.5" /> Recommended Actions
                  </div>
                  <ol className="list-decimal pl-5 space-y-1.5">
                    {content.recommended_actions.map((a, i) => (
                      <li key={i} className="text-sm text-slate-800 leading-relaxed font-medium">{a}</li>
                    ))}
                  </ol>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[10px] text-slate-500">
          <div>
            Synthesised from {data?.committee_aggregate?.fully_scored_count ?? 6}/6 committee scores · Scientific Assessment · ForesightRx BD Opportunity
          </div>
          <div>
            {data?.generated_at && (
              <>Generated {new Date(data.generated_at).toLocaleString()}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-700 font-semibold mb-1.5 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-indigo-600" />} {title}
      </div>
      <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}


function DimensionList({ icon: Icon, tone, title, items, testid }) {
  const palette = tone === 'emerald'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-rose-50 border-rose-200 text-rose-900';
  return (
    <div className={`border rounded p-3 ${palette}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide font-semibold mb-1.5 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {title}
      </div>
      <ul className="text-xs space-y-0.5">
        {(items || []).map((it, i) => (<li key={i}>· {it}</li>))}
      </ul>
    </div>
  );
}


function BulletBox({ tone, title, items, testid }) {
  const palette = tone === 'emerald'
    ? 'border-emerald-200 bg-emerald-50/40 text-emerald-900'
    : 'border-rose-200 bg-rose-50/40 text-rose-900';
  return (
    <section className={`border rounded p-3 ${palette}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide font-semibold mb-2">{title}</div>
      <ul className="text-sm space-y-1 list-disc pl-5 text-slate-700">
        {(items || []).length === 0 ? (
          <li className="italic text-slate-400 list-none pl-0">No items called out by the committee.</li>
        ) : (
          items.map((it, i) => (<li key={i}>{it}</li>))
        )}
      </ul>
    </section>
  );
}
